// jobTerminalToast — shared terminal-outcome notification for pipeline_jobs.
// -----------------------------------------------------------------
// The stage-aware "job complete / failed / cancelled / quota" copy used to live
// inline in `usePipelineJobTerminalToast` (PipelineJobContext). App-level job
// notifications (the global <JobNotifierProvider>) need the SAME copy for jobs
// that finish AFTER the operator has navigated away from the launching screen,
// so this module is the single home for:
//
//   - `toastStageNoun`      — command → human noun ("Audio" / "Translation" …);
//   - `buildJobTerminalToast` — pure: terminal job → notification config | null;
//   - `announceJobTerminal` — fire the Mantine toast (tier A) AND, when the tab
//     is backgrounded, an OS notification (tier B) with the same copy.
//
// Both the (running → terminal) hook and the global watcher call
// `announceJobTerminal`, so the copy + the tier-A/tier-B fan-out stay in lockstep.

import { notifications } from '@mantine/notifications';

import { maybeNotifyDesktop } from '@/lib/notify/desktopNotification';
import { type PipelineJob } from '@/lib/types/pipelineJob';

/** Stage-aware noun for the terminal notification copy, derived from
 *  `pipeline_jobs/<id>.command`. Command-less jobs (legacy / test fixtures)
 *  fall back to the historical "TTS" copy so the toast-copy contract stays
 *  green. */
export function toastStageNoun(command: string | undefined): string {
  switch (command) {
    case 'translate':
      return 'Translation';
    case 'translate_metadata':
      // M-Pipeline.4b B3: metadata localization job (FR → targets).
      return 'Metadata localization';
    case 'format_tts':
      return 'TTS formatting';
    case 'generate_audio':
      return 'Audio';
    case 'generate_base_script':
      return 'Script generation';
    case 'generate_research':
      // Job-command value the backend writes for the research worker (the
      // `request_perplexity` UI action dispatches it).
      return 'Research';
    default:
      return 'TTS';
  }
}

/** The notification payload for a terminal job (shape shared by the Mantine
 *  toast + the OS notification). */
export type JobTerminalNotification = {
  color: string;
  autoClose: number | false;
  title: string;
  message: string;
};

/**
 * Pure: map a terminal `pipeline_jobs/<id>` doc to its notification config, or
 * `null` when the job is still running / not loaded (non-terminal). No side
 * effects — {@link announceJobTerminal} does the firing.
 */
export function buildJobTerminalToast(
  job: PipelineJob | undefined,
): JobTerminalNotification | null {
  const status = job?.status;
  if (!status || status === 'running') return null;

  const nDone = job?.progress.n_done ?? 0;
  const nTotal = job?.progress.n_total ?? 0;
  // pipeline_jobs result items stamp `worker_status` ('failed' is the error key).
  const errorCount =
    job?.results.filter((r) => r.worker_status === 'failed').length ?? 0;
  const noun = toastStageNoun(job?.command);

  switch (status) {
    case 'done':
      return {
        color: errorCount > 0 ? 'yellow' : 'green',
        autoClose: 8000,
        title:
          errorCount > 0
            ? `${noun} job complete with failures`
            : `${noun} job complete`,
        message:
          errorCount > 0
            ? `${nDone - errorCount} of ${nTotal} items completed. ${errorCount} failed — check the pipeline panel for details.`
            : `${nTotal} items completed successfully.`,
      };
    case 'cancelled':
      return {
        color: 'yellow',
        autoClose: 8000,
        title: `${noun} job cancelled`,
        message: `${nDone} of ${nTotal} items completed before cancel.`,
      };
    case 'quota_exhausted':
      return {
        color: 'yellow',
        autoClose: false,
        title: 'Gemini daily quota reached',
        message: `${nDone} of ${nTotal} items completed. Quota resets at 00:00 UTC. Resume tomorrow or click Reset on the pipeline panel.`,
      };
    case 'error':
      return {
        color: 'red',
        autoClose: false,
        title: `${noun} job failed`,
        message: `${job?.error ?? 'Unknown error'}. Check the pipeline panel + worker CF logs.`,
      };
    default:
      return null;
  }
}

/**
 * Fire the terminal notification for `job`: the in-app Mantine toast (tier A,
 * always) and — when the tab is backgrounded + permission granted — an OS
 * notification (tier B). No-op for a non-terminal job.
 *
 * `tag` (typically the job id) collapses a duplicate OS notification for the
 * same job.
 */
export function announceJobTerminal(
  job: PipelineJob | undefined,
  tag?: string,
): void {
  const cfg = buildJobTerminalToast(job);
  if (!cfg) return;
  notifications.show({
    color: cfg.color,
    autoClose: cfg.autoClose,
    title: cfg.title,
    message: cfg.message,
  });
  maybeNotifyDesktop({
    title: cfg.title,
    body: cfg.message,
    ...(tag !== undefined ? { tag } : {}),
  });
}
